// MetaTagManager.js - Utility for managing meta tags on the client side
class MetaTagManager {
  static updateMetaTags(metaData) {
    // Update title
    if (metaData.title) {
      document.title = metaData.title;
    }

    // Helper function to update or create meta tag
    const updateMetaTag = (selector, content, property = 'content') => {
      if (!content) return;
      
      let meta = document.querySelector(selector);
      if (meta) {
        meta.setAttribute(property, content);
      } else {
        meta = document.createElement('meta');
        // Extract attribute name and value from selector
        const match = selector.match(/\[([^=]+)="([^"]+)"\]/);
        if (match) {
          meta.setAttribute(match[1], match[2]);
          meta.setAttribute(property, content);
          document.head.appendChild(meta);
        }
      }
    };

    // Update description
    updateMetaTag('meta[name="description"]', metaData.description);

    // Update Open Graph tags
    updateMetaTag('meta[property="og:title"]', metaData.title);
    updateMetaTag('meta[property="og:description"]', metaData.description);
    updateMetaTag('meta[property="og:image"]', metaData.socialImage);
    updateMetaTag('meta[property="og:image:secure_url"]', metaData.socialImage);
    updateMetaTag('meta[property="og:image:alt"]', metaData.imageAlt);
    updateMetaTag('meta[property="og:url"]', metaData.url);

    // Update Twitter tags
    updateMetaTag('meta[name="twitter:title"]', metaData.title);
    updateMetaTag('meta[name="twitter:description"]', metaData.description);
    updateMetaTag('meta[name="twitter:image"]', metaData.socialImage);
    updateMetaTag('meta[name="twitter:image:alt"]', metaData.imageAlt);

    // Update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical && metaData.url) {
      canonical.setAttribute('href', metaData.url);
    } else if (metaData.url) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      canonical.setAttribute('href', metaData.url);
      document.head.appendChild(canonical);
    }

    // Update structured data
    if (metaData.structuredData) {
      let structuredDataScript = document.querySelector('script[type="application/ld+json"]');
      if (structuredDataScript) {
        structuredDataScript.textContent = JSON.stringify(metaData.structuredData);
      } else {
        structuredDataScript = document.createElement('script');
        structuredDataScript.setAttribute('type', 'application/ld+json');
        structuredDataScript.textContent = JSON.stringify(metaData.structuredData);
        document.head.appendChild(structuredDataScript);
      }
    }
  }

  static generateMemorialMetaTags(memorial, slug) {
    const baseUrl = window.location.origin;
    
    let socialImage = `https://via.placeholder.com/1200x630/1a202c/ffffff/png?text=${encodeURIComponent('SouleMap Memorial')}`;
    let title = 'Memorial | SouleMap';
    let description = 'A beautiful memorial tribute preserving memories and celebrating a life well lived.';
    let imageAlt = 'Memorial tribute';
    
    if (memorial) {
      const firstName = this.capitalizeNames(memorial.firstName) || '';
      const lastName = this.capitalizeNames(memorial.lastName) || '';
      const fullName = `${firstName} ${lastName}`.trim();
      
      title = fullName 
        ? `${fullName} - Memorial | SouleMap`
        : 'Memorial | SouleMap';
      
      if (fullName) {
        description = `In loving memory of ${fullName}. Share memories, leave digital flowers, and celebrate a beautiful life well lived. Memorial created with love on SouleMap.`;
      }
      
      imageAlt = fullName 
        ? `Memorial tribute for ${fullName}` 
        : 'Memorial tribute';
      
      // Use memorial images if available  
      if (memorial.images && memorial.images.length > 0) {
        socialImage = this.optimizeFirebaseImageUrl(memorial.images[0]);
      } else if (memorial.bannerImage) {
        socialImage = this.optimizeFirebaseImageUrl(memorial.bannerImage);
      } else {
        // Enhanced placeholder with memorial name
        const nameForPlaceholder = fullName || 'Memorial';
        socialImage = `https://via.placeholder.com/1200x630/2d3748/ffffff/png?text=${encodeURIComponent(nameForPlaceholder)}%0A${encodeURIComponent('Memorial Tribute')}`;
      }
    }
    
    const url = `${baseUrl}/memorial/${slug}`;
    
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Person",
      "name": memorial ? `${this.capitalizeNames(memorial.firstName)} ${this.capitalizeNames(memorial.lastName)}`.trim() : 'Memorial',
      "url": url,
      "image": socialImage,
      "description": description,
      "memorialOf": {
        "@type": "Person",
        "name": memorial ? `${this.capitalizeNames(memorial.firstName)} ${this.capitalizeNames(memorial.lastName)}`.trim() : 'Memorial'
      }
    };

    if (memorial?.birthDate) {
      structuredData.memorialOf.birthDate = memorial.birthDate;
    }
    if (memorial?.deathDate) {
      structuredData.memorialOf.deathDate = memorial.deathDate;
    }

    return { 
      title, 
      description, 
      socialImage, 
      url, 
      imageAlt,
      structuredData
    };
  }

  static capitalizeNames(name) {
    if (!name) return '';
    return name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  static optimizeFirebaseImageUrl(firebaseUrl) {
    if (!firebaseUrl) return firebaseUrl;
    
    try {
      // If it's already a direct media URL, return as-is
      if (firebaseUrl.includes('alt=media')) {
        return firebaseUrl;
      }
      
      const bucketMatch = firebaseUrl.match(/firebasestorage\.googleapis\.com\/v0\/b\/([^\/]+)/);
      const pathMatch = firebaseUrl.match(/\/o\/([^?]+)/);
      
      if (bucketMatch && pathMatch) {
        const bucket = bucketMatch[1];
        const path = decodeURIComponent(pathMatch[1]);
        return `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodeURIComponent(path)}?alt=media`;
      } else {
        return firebaseUrl;
      }
    } catch (error) {
      console.error('Error optimizing Firebase URL:', error);
      return firebaseUrl;
    }
  }

  static resetToDefault() {
    const defaultMetaTags = {
      title: 'SouleMap - The Social Memorial',
      description: 'Create beautiful digital memorials to honor and remember loved ones. Share memories, photos, and stories in a permanent online tribute.',
      socialImage: `${window.location.origin}/social-image.png`,
      url: window.location.href,
      imageAlt: 'SouleMap - The Social Memorial'
    };

    this.updateMetaTags(defaultMetaTags);
  }
}

export default MetaTagManager;
